/*
 * THE PLACES — where the opening flight goes, and where a reader is left standing.
 *
 * NOT A TRADITION. A PLACE. The design is emphatic and the distinction is the point: the last
 * thing this map should do is ask which book the reader holds. So it names neighbourhoods — a
 * figure, and the names that come into reach by standing where they stand. Each is the middle of
 * somebody's story, and several are the middle of more than one tradition's.
 *
 * DERIVED, NOT CURATED. The design hard-codes six zones ("The Flood", "Egypt and the Exodus") with
 * a seed figure and a sentence apiece, written against its own 300-name roster. Retyping that list
 * here would be a list that goes stale the day the roster changes, and worse, it would name places
 * this library may not carry. So the seeds are found: the busiest neighbourhoods on the map, kept
 * apart so that no two of them are the same crowd seen twice.
 *
 * It lives in core because "where can a reader begin" is a fact about the tie graph, exactly as
 * `journey.ts`'s "where can they go next" is — one place to decide it, testable without a browser,
 * and the same answer for both renderers.
 */

/** What this reads from a node. Narrower than `EntityNode`, which is assignable to it. */
export interface ZoneInput {
  id: string;
  name: string;
  /** What kind of thing it is — see `ENTITY_KINDS`. */
  type: string;
  /** Where it settled, so a camera can be flown to it. */
  x: number;
  y: number;
  /** The texts that name it, so a place can say where its story is told. */
  corpora: readonly string[];
  /** Every name tied to this one. The neighbourhood that comes into reach by beginning here. */
  ties: readonly string[];
}

/** One place to begin, and what standing there opens. */
export interface Zone {
  id: string;
  name: string;
  /** Where it is on the map. A place a camera cannot be pointed at is not a place. */
  x: number;
  y: number;
  corpora: readonly string[];
  /** How many names stand within one step — what comes into reach by beginning here. */
  reach: number;
}

/**
 * How many places are found by default.
 *
 * More than any one surface shows, on purpose: this is the ranked list, and each caller takes the
 * prefix it has room for — the opening flight visits three, which is as many place-names as a
 * reader will sit through before touching anything.
 */
export const ZONE_COUNT = 6;

/**
 * THE NAMES THAT ARE NOT A PLACE TO BEGIN.
 *
 * The names of God are the most-tied nodes on this map by a wide margin — every scripture's every
 * figure stands in some relation to them — so by degree alone they would take every place. But the
 * centre of this map is deliberately not a story: it is where the traditions meet, and "begin at
 * God" is precisely the tradition question the arrival exists not to ask. The flight names the
 * centre on its way past; it does not leave anybody standing there.
 *
 * Everything else stays eligible. A place is a fine place to begin, and so is a people.
 */
const NOT_A_PLACE = new Set(['divine', 'deity']);

/**
 * Find the places to begin: the busiest neighbourhoods, no two of them adjacent.
 *
 * ADJACENCY IS THE SEPARATION RULE, and it is doing real work. The most-tied names cluster —
 * Abraham, Isaac and Jacob are each other's neighbours — so by degree alone the flight would visit
 * one crowd three times. A candidate already within reach of a chosen seed is not a different place
 * to begin; it is the same place, named twice.
 *
 * Deterministic: sorted by reach and then by id, so the same roster gives the same six every
 * build. A reader who left and came back to a reshuffled set of beginnings would read it as the
 * map having changed underneath them.
 *
 * Returns FEWER than `count` rather than filling the gap with an adjacent name — a place that
 * duplicates its neighbour's crowd is worse than one place fewer.
 */
export function zonesFor(nodes: readonly ZoneInput[], count = ZONE_COUNT): Zone[] {
  const eligible = nodes
    .filter(n => !NOT_A_PLACE.has(n.type) && n.ties.length > 0)
    .slice()
    .sort((a, b) => (b.ties.length - a.ties.length) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  const taken: Zone[] = [];
  /* Every name already within reach of something chosen — the whole of what is spoken for. */
  const spokenFor = new Set<string>();
  for (const node of eligible) {
    if (taken.length >= count) break;
    if (spokenFor.has(node.id)) continue;
    taken.push({ id: node.id, name: node.name, x: node.x, y: node.y, corpora: node.corpora, reach: node.ties.length });
    spokenFor.add(node.id);
    for (const tie of node.ties) spokenFor.add(tie);
  }
  return taken;
}
